// Pure helpers describing when the Document form's primary action buttons
// (Update / Confirm / Post) may be interacted with.
//
// This module is intentionally dependency-free so it can be imported by the
// DocumentForm component AND unit-tested in isolation.
//
// Background: for large imports (e.g. Transfer Allocation with 500+ lines) the
// form becomes visible before initializeDocument() has finished the synchronous
// work of building the table and snapshotting the dataset. During that window a
// user click on Update/Confirm/Post would be queued behind the busy main thread
// and appear to "do nothing". To fix this the buttons must stay disabled — and a
// blocking loading overlay must be shown — for the entire duration of loading.
//
// The rule is deliberately simple and additive: a button is disabled when its
// own existing disabled flag is set OR the document is still initializing. This
// preserves all previous disabling behaviour and only adds the loading guard.

/**
 * Returns true when a document action button should be disabled.
 *
 * @param {boolean} ownDisabledFlag - the button's pre-existing disabled state
 *   (e.g. isUpdateDisabled / isConfirmDisabled / isPostDisabled).
 * @param {boolean} isInitializing - true while initializeDocument() is loading
 *   and building the form.
 * @returns {boolean}
 */
function isDocumentActionDisabled(ownDisabledFlag, isInitializing) {
  return Boolean(ownDisabledFlag) || Boolean(isInitializing);
}

/**
 * Returns true when the blocking loading overlay should be visible.
 *
 * The overlay is shown while the document is initializing so the user gets a
 * clear visual cue and cannot click the action buttons until loading completes.
 *
 * @param {boolean} isInitializing
 * @returns {boolean}
 */
function shouldShowLoadingOverlay(isInitializing) {
  return Boolean(isInitializing);
}

export { isDocumentActionDisabled, shouldShowLoadingOverlay };
